<template>
  <v-container class="grey-lighten-3 full-height">

    <v-row justify="center">

      <v-col cols="12" xl="10">

        <v-sheet
          class="report-paper pa-6"
          elevation="3"
          id="printable-area"
        >

          <!-- WATERMARK -->
          <div
            class="watermark"
            v-if="details?.branch?.company?.watermark && invoiceHeader"
          >
            <img
              :src="`${getFileServerUrl}${details?.branch?.company?.watermark}`"
              alt=""
            >
          </div>

          <div class="invoice-content">

            <table style="width:100%; border-collapse: collapse;">

              <!-- HEADER -->
              <thead>

                <tr
                  v-if="details?.branch?.company?.header_image && invoiceHeader"
                >

                  <td colspan="100%">

                    <img
                      :src="`${getFileServerUrl}${details?.branch?.company?.header_image}`"
                      style="width:100%;"
                      alt=""
                    >

                  </td>

                </tr>

              </thead>

              <tbody>

                <!-- DEFAULT HEADER -->
                <tr
                  v-if="!details?.branch?.company?.header_image && invoiceHeader"
                >

                  <td colspan="100%">

                    <div class="page-header">

                      <div class="d-flex justify-space-between align-start">

                        <!-- COMPANY -->
                        <div>

                          <h4 class="company-name">
                            {{ details?.branch?.company?.name || '' }}
                          </h4>

                          <p class="company-text">
                            {{ details?.branch?.company?.address || '' }}
                          </p>

                          <p class="company-text">
                            <v-icon size="small">mdi-phone-outline</v-icon>
                            {{ details?.branch?.company?.mobile || '' }}
                          </p>

                          <p class="company-text">
                            <v-icon size="small">mdi-email-outline</v-icon>
                            {{ details?.branch?.company?.email || '' }}
                          </p>

                        </div>

                        <!-- LOGO -->
                        <div>

                          <v-img
                            :src="`${getFileServerUrl}/${details?.branch?.company?.logo || 'uploads/demo_images/user.png'}`"
                            max-width="150"
                            max-height="70"
                            contain
                          />

                        </div>

                      </div>

                    </div>

                  </td>

                </tr>

                <!-- TITLE -->
                <tr>

                  <td colspan="100%">

                    <div class="text-center my-6">

                      <h2 class="report-title">
                        PRICE CALCULATION DETAILS
                      </h2>

                    </div>

                  </td>

                </tr>

                <!-- MASTER INFO -->
                <tr>

                  <td colspan="100%">

                    <div class="master-box">

                      <table class="dep-table">

                        <tbody>

                          <tr>

                            <td width="25%">
                              <strong>Inquiry Code</strong>
                            </td>

                            <td width="25%">
                              {{ details?.inquiry_master?.code || 'N/A' }}
                            </td>

                            <td width="25%">
                              <strong>HS Code</strong>
                            </td>

                            <td width="25%">
                              {{ details?.hs_code || 'N/A' }}
                            </td>

                          </tr>

                          <tr class="d-none">

                            <td>
                              <strong>Customer</strong>
                            </td>

                            <td>
                              {{ details?.inquiry_master?.customer?.company_name || 'N/A' }}
                            </td>

                            <td>
                              <strong>Customer Mobile</strong>
                            </td>

                            <td>
                              {{ details?.inquiry_master?.customer?.company_mobile || 'N/A' }}
                            </td>

                          </tr>

                          <tr>

                            <td>
                              <strong>Lead</strong>
                            </td>

                            <td>
                              {{ details?.inquiry_master?.lead ? (details?.inquiry_master?.lead?.code+'-'+details?.inquiry_master?.lead?.company_name) : 'N/A' }}
                            </td>

                            <td>
                              <strong>Product</strong>
                            </td>

                            <td>
                              {{ details?.inquiry_detail?.product?.name || 'N/A' }}
                            </td>

                          </tr>

                          <tr>

                            <td>
                              <strong>Product Code</strong>
                            </td>

                            <td>
                              {{ details?.inquiry_detail?.product?.code || 'N/A' }}
                            </td>

                            <td>
                              <strong>Status</strong>
                            </td>

                            <td>
                              {{ details?.status == 1 ? 'Published' : 'Draft' }}
                            </td>

                          </tr>

                        </tbody>

                      </table>

                    </div>

                  </td>

                </tr>

                <!-- CALCULATION TABLE -->
                <tr>

                  <td colspan="100%">

                    <table class="dep-table mt-5">

                      <thead>

                        <tr>

                          <th>Assessment Value</th>

                          <th>TTI</th>

                          <th>Shipping Cost</th>

                          <th>Tax / KG</th>

                          <th>Total Tax</th>

                          <th>Other Cost</th>

                          <th>Profit</th>

                          <th>Proposed Price</th>

                        </tr>

                      </thead>

                      <tbody>

                        <tr>

                          <td class="text-right">
                            {{ formatNumber(details?.assessment_value) }}
                          </td>

                          <td class="text-right">
                            {{ formatNumber(details?.tti) }}
                          </td>

                          <td class="text-right">
                            {{ formatNumber(details?.shipping_cost) }}
                          </td>

                          <td class="text-right">
                            {{ formatNumber(details?.tax_per_kg) }}
                          </td>

                          <td class="text-right">
                            {{ formatNumber(details?.total_tax) }}
                          </td>

                          <td class="text-right">
                            {{ formatNumber(details?.other_cost) }}
                          </td>

                          <td class="text-right">
                            {{ formatNumber(details?.profit) }}
                          </td>

                          <td class="text-right">
                            {{ formatNumber(details?.proposed_price) }}
                          </td>

                        </tr>

                      </tbody>

                    </table>

                  </td>

                </tr>

                <!-- PRODUCT INFO -->
                <tr>

                  <td colspan="100%">

                    <table class="dep-table mt-5">

                      <thead>

                        <tr>

                          <th>Net Weight</th>

                          <th>Gross Weight</th>

                          <th>CBM</th>

                          <th>Remarks</th>

                        </tr>

                      </thead>

                      <tbody>

                        <tr>

                          <td class="text-right">
                            {{ formatNumber(details?.inquiry_detail?.net_weight) }}
                          </td>

                          <td class="text-right">
                            {{ formatNumber(details?.inquiry_detail?.gross_weight) }}
                          </td>

                          <td class="text-right">
                            {{ formatNumber(details?.inquiry_detail?.cbm) }}
                          </td>

                          <td>
                            {{ details?.inquiry_detail?.remarks || '-' }}
                          </td>

                        </tr>

                      </tbody>

                    </table>

                  </td>

                </tr>

              </tbody>

              <tfoot>

                <tr>

                  <td colspan="100%">

                    <div style="height:100px;"></div>

                  </td>

                </tr>

              </tfoot>

            </table>

          </div>

        </v-sheet>

      </v-col>

    </v-row>

    <!-- PRINT BUTTON -->
    <v-row class="d-print-none mt-4">

      <v-col
        cols="12"
        class="d-flex justify-center"
      >

        <v-btn
          color="primary"
          prepend-icon="mdi-printer"
          @click="print(0)"
        >
          Print without pad
        </v-btn>

        <v-btn
          color="primary"
          class="ml-2"
          prepend-icon="mdi-printer"
          @click="print(1)"
        >
          Print with pad
        </v-btn>

      </v-col>

    </v-row>

  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";

const store = useStore();
const route = useRoute();

const details = ref({});
const invoiceHeader = ref(true);
const pagePadding = ref(0);

const getFileServerUrl = computed(() => {
  return store.getters.getFileServerUrl;
});

const formatNumber = (val) => {

  if (val == null) return "";

  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

};

const print = (isPad) => {

  invoiceHeader.value = isPad === 1;

  pagePadding.value = isPad === 1 ? 0 : 100;

  setTimeout(() => {

    const printContent = document.getElementById("printable-area").outerHTML;

    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <html>
        <head>
          <title>Price Calculation Details</title>

          <style>

            body{
              margin:0;
              font-family:sans-serif;
            }

            table{
              width:100%;
              border-collapse:collapse;
            }

            th,td{
              border:1px solid #000;
              padding:4px;
              font-size:10px;
            }

            th{
              background:#eee;
            }

            @page{
              size:A4;
              margin:${pagePadding.value}px 10px;
            }

          </style>

        </head>

        <body>

          ${printContent}

        </body>

      </html>
    `);

    printWindow.document.close();

    setTimeout(() => {

      printWindow.print();

      printWindow.close();

    }, 500);

  }, 300);

};

const loadData = async () => {

  try {

    const res = await store.dispatch(
      "getPriceCalculationById",
      route.params.id
    );

    if (res?.success) {

      details.value = res.data;

    }

  } catch (error) {

    console.error(error);

  }

};

onMounted(async () => {
  await loadData();
});
</script>

<style scoped>

.full-height{
  min-height:60vh;
}

.grey-lighten-3{
  background:#eeeeee;
}

.report-paper{
  background:white;
}

.invoice-content{
  width:100%;
  overflow:hidden;
}

.company-name{
  font-size:9px;
  font-weight:bold;
}

.company-text{
  font-size:9px;
}

.report-title{
  font-size:16px;
  text-align:center;
}

.dep-table{
  width:100%;
  border-collapse:collapse;
  font-size:9px;
}

.dep-table th,
.dep-table td{
  border:1px solid #000;
  padding:4px;
}

.text-center{
  text-align:center;
}

.text-right{
  text-align:right;
}

.watermark{
  position:absolute;
  top:50%;
  left:50%;
  opacity:0.1;
  transform:translate(-50%,-50%);
}

.master-box{
  margin-top:10px;
}

.d-print-none{
  display:block;
}

@media print {

  .d-print-none{
    display:none;
  }

}
</style>